import { Link } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PUBLIC_ROUTES } from "@/constants/routes";
import type { ProductSeller } from "@/features/marketplace/types/product";

interface SellerCardProps {
  seller: ProductSeller;
}

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

/**
 * docs/ui/component-inventory.md `SellerCard` (compact variant, inline on
 * Product Details). No star rating — docs/database/users.md lists
 * `sellerRating` as a "Future Field," so there's nothing real to render
 * yet; adding a fake/zero rating would be worse than omitting it. Links to
 * `/profile/:id` (the whole card is a click target) now that the public
 * profile view exists — `itemsListedCount` only renders when present,
 * since the list endpoint's seller summary doesn't include it (only the
 * single-product detail response does).
 */
export function SellerCard({ seller }: SellerCardProps) {
  const memberSinceYear = new Date(seller.memberSince).getFullYear();

  return (
    <Link
      to={PUBLIC_ROUTES.sellerProfile.replace(":userId", seller.id)}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:bg-muted"
    >
      <Avatar size="md">
        {seller.avatarUrl && <AvatarImage src={seller.avatarUrl} alt="" />}
        <AvatarFallback>{initialsFromName(seller.name)}</AvatarFallback>
      </Avatar>
      <div>
        <p className="text-body font-medium text-foreground">{seller.name}</p>
        <p className="text-body-sm text-muted-foreground">
          Member since {memberSinceYear}
          {seller.itemsListedCount !== undefined &&
            ` · ${seller.itemsListedCount} item${seller.itemsListedCount === 1 ? "" : "s"} listed`}
        </p>
      </div>
    </Link>
  );
}
